import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const inp = { width: '100%', padding: '0.625rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: theme.white, outline: 'none', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' };
const sectionHdr = { fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0.25rem 0 0.875rem', paddingBottom: '0.4rem', borderBottom: `1px solid ${theme.border}` };

function Field({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={lbl}>{label}</label>
      {children}
    </div>
  );
}

const EMPTY_FORM = {
  name: '', category_id: '', make: '', model: '', serial_number: '',
  purchase_date: '', purchase_cost: '', warranty_expiry: '',
  status: 'available', location: '', notes: '',
  cpu: '', ram_gb: '', storage_gb: '', os: '', screen_size: '',
  custom_specs: '', inspection_grade: '',
};

function AssetForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';

  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // New-asset code generation
  const [prefix, setPrefix] = useState('BT');
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    it.getAssetCategories().then((r) => setCategories(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
    if (isEdit) {
      it.getAsset(id).then((r) => {
        const a = r.data;
        setForm({
          name: a.name || '',
          category_id: a.category_id || '',
          make: a.make || '',
          model: a.model || '',
          serial_number: a.serial_number || '',
          purchase_date: a.purchase_date || '',
          purchase_cost: a.purchase_cost || '',
          warranty_expiry: a.warranty_expiry || '',
          status: a.status || 'available',
          location: a.location || '',
          notes: a.notes || '',
          cpu: a.cpu || '',
          ram_gb: a.ram_gb || '',
          storage_gb: a.storage_gb || '',
          os: a.os || '',
          screen_size: a.screen_size || '',
          custom_specs: a.custom_specs || '',
          inspection_grade: a.inspection_grade || '',
        });
      }).catch(() => setError('Failed to load asset.'));
    }
  }, [id, isEdit]);

  // Auto-generate code whenever prefix or category changes (new asset only)
  useEffect(() => {
    if (isEdit || !prefix || !form.category_id) { setGeneratedCode(''); return; }
    setCodeLoading(true);
    it.getNextAssetCode(prefix, form.category_id)
      .then((r) => setGeneratedCode(r.data.code))
      .catch(() => setGeneratedCode(''))
      .finally(() => setCodeLoading(false));
  }, [prefix, form.category_id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { setError('Asset name is required.'); return; }
    if (!isEdit && !generatedCode) { setError('Select a prefix and category to generate the asset code.'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        category_id: form.category_id ? Number(form.category_id) : null,
        purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null,
        ram_gb: form.ram_gb ? Number(form.ram_gb) : null,
        storage_gb: form.storage_gb ? Number(form.storage_gb) : null,
        purchase_date: form.purchase_date || null,
        warranty_expiry: form.warranty_expiry || null,
        cpu: form.cpu || null,
        os: form.os || null,
        screen_size: form.screen_size || null,
        custom_specs: form.custom_specs || null,
        inspection_grade: form.inspection_grade || null,
      };
      if (!isEdit) payload.asset_code = generatedCode;
      if (isEdit) {
        await it.updateAsset(id, payload);
      } else {
        await it.createAsset(payload);
      }
      navigate('/it/assets');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save asset.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: '860px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <Button variant="outlineGray" onClick={() => navigate('/it/assets')}>← Back</Button>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>
            {isEdit ? 'Edit Asset' : 'Add New Asset'}
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {isEdit ? 'Update asset information' : 'Register a new IT asset'}
          </p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── Identity ─────────────────────────────── */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <p style={sectionHdr}>Asset Identity</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>

            {/* Step 1: Category (required first) */}
            <Field label="Category *">
              <select style={inp} value={form.category_id} onChange={(e) => set('category_id', e.target.value)} required>
                <option value="">Select category first…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            {/* Step 2: Prefix dropdown (new assets only) */}
            {!isEdit && (
              <Field label="Asset ID Prefix *">
                <select style={inp} value={prefix} onChange={(e) => setPrefix(e.target.value)}>
                  <option value="BT">BT — Bonton</option>
                  <option value="VT">VT — VTT</option>
                </select>
              </Field>
            )}

            {/* Generated Asset ID (readonly) */}
            {!isEdit && (
              <Field label="Asset ID (Auto-generated)" span={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    style={{ ...inp, background: '#F3F4F6', color: theme.primary, fontWeight: 700, fontFamily: 'monospace', cursor: 'not-allowed', flex: 1 }}
                    value={codeLoading ? 'Generating…' : generatedCode || '— select category & prefix —'}
                    readOnly
                  />
                  {generatedCode && (
                    <span style={{ fontSize: '0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>✓ Ready</span>
                  )}
                </div>
              </Field>
            )}

            <Field label="Asset Name *" span={2}>
              <input style={inp} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Dell Latitude 5530" required />
            </Field>

            <Field label="Status">
              <select style={inp} value={form.status} onChange={(e) => set('status', e.target.value)}>
                <option value="available">Available</option>
                <option value="assigned">Assigned</option>
                <option value="in_repair">In Repair</option>
                <option value="retired">Retired</option>
                <option value="lost">Lost</option>
                <option value="disposed">Disposed</option>
              </select>
            </Field>
            <Field label="Make / Brand">
              <input style={inp} value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="Dell, Apple, HP..." />
            </Field>
            <Field label="Model">
              <input style={inp} value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="Latitude 5530, MacBook Pro 14..." />
            </Field>
            <Field label="Serial Number">
              <input style={inp} value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} placeholder="SN-XXXXXXXXX" />
            </Field>
            <Field label="Location">
              <input style={inp} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Office / Floor 2 / Storage..." />
            </Field>
            <Field label="Purchase Date">
              <input style={inp} type="date" value={form.purchase_date} onChange={(e) => set('purchase_date', e.target.value)} />
            </Field>
            <Field label="Warranty Expiry">
              <input style={inp} type="date" value={form.warranty_expiry} onChange={(e) => set('warranty_expiry', e.target.value)} />
            </Field>
            <Field label="Inspection Grade">
              <select style={inp} value={form.inspection_grade} onChange={(e) => set('inspection_grade', e.target.value)}>
                <option value="">Not Inspected</option>
                <option value="A">A — Excellent</option>
                <option value="B">B — Good</option>
                <option value="C">C — Fair</option>
                <option value="D">D — Poor</option>
              </select>
            </Field>
          </div>
        </Card>

        {/* ── Specifications ───────────────────────── */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <p style={sectionHdr}>Technical Specifications</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <Field label="Processor / CPU">
              <input style={inp} value={form.cpu} onChange={(e) => set('cpu', e.target.value)} placeholder="Intel Core i7-1265U" />
            </Field>
            <Field label="RAM (GB)">
              <input style={inp} type="number" min="0" value={form.ram_gb} onChange={(e) => set('ram_gb', e.target.value)} placeholder="16" />
            </Field>
            <Field label="Storage (GB)">
              <input style={inp} type="number" min="0" value={form.storage_gb} onChange={(e) => set('storage_gb', e.target.value)} placeholder="512" />
            </Field>
            <Field label="Operating System">
              <input style={inp} value={form.os} onChange={(e) => set('os', e.target.value)} placeholder="Windows 11 Pro" />
            </Field>
            <Field label="Screen Size">
              <input style={inp} value={form.screen_size} onChange={(e) => set('screen_size', e.target.value)} placeholder='14"' />
            </Field>
            <Field label="Custom Specs (JSON or notes)" span={2}>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                value={form.custom_specs}
                onChange={(e) => set('custom_specs', e.target.value)}
                placeholder='{"GPU": "Intel Iris Xe", "Battery": "60Wh", "Ports": "2x USB-A, 1x HDMI"}' />
            </Field>
          </div>
        </Card>

        {/* ── Notes ───────────────────────────────── */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <p style={sectionHdr}>Additional Notes</p>
          <textarea style={{ ...inp, resize: 'vertical', minHeight: '90px' }}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Any additional notes about this asset..." />
        </Card>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <Button type="button" variant="outlineGray" onClick={() => navigate('/it/assets')}>Cancel</Button>
          <Button type="submit" variant="primary" loading={loading}>{isEdit ? 'Update Asset' : 'Add Asset'}</Button>
        </div>
      </form>
    </div>
  );
}

export default AssetForm;
